import clsx from "clsx";
import dayjs from "dayjs";
import { IRentalRequestPcComponent, PcComponent, RentalRequestStatus } from "@app/types";
import { useT } from "@app/contexts/preferences.context";
import Pagination from "@app/components/pagination";
import { IcPackage, IcRefresh } from "@app/components/icons";
import { CopyButton, LoadingBlock, StatusPill, btn, btnGhost, btnSm } from "@app/components/ui";
import { categoryOf } from "./pc-parts";

/** Nhãn và màu cho enum status của yêu cầu */
const STATUS: Record<RentalRequestStatus, [string, "ok" | "warn" | "bad" | "muted"]> = {
  Mới: ["Mới gửi", "warn"],
  Duyệt: ["Đã duyệt", "ok"],
  "Ship Hàng": ["Đang giao hàng", "ok"],
  "Khách Đã Nhận Hàng/Thanh Toán": ["Đã nhận hàng", "ok"],
  "Khách Đã Trả Hàng/Hoàn Cọc": ["Đã trả, hoàn cọc", "muted"],
  Hủy: ["Đã huỷ", "bad"],
};

interface PcRequestsProps {
  /** Chưa có dữ liệu lần đầu */
  loading?: boolean;
  requests: IRentalRequestPcComponent[];
  count: number;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onAgain: (component: PcComponent) => void;
}

/** "Yêu cầu đã gửi": GET /rental-requests/me (componentId đã populate) */
function PcRequests({ requests, count, page, totalPages, onPageChange, onAgain, loading }: PcRequestsProps) {
  const t = useT();

  return (
    <section id="pc-requests" className="scroll-mt-20 overflow-hidden rounded-[14px] border border-line bg-surface">
      <div className="border-b border-line px-5 py-4 max-[760px]:px-3.5">
        <h2 className="flex items-center gap-2 text-[17px] font-bold">
          {t("Yêu cầu đã gửi")}
          <span className="rounded-full bg-brand-soft px-[9px] py-px text-xs font-semibold text-brand">{count}</span>
        </h2>
        <p className="m-0 mt-0.5 text-[13px] text-muted">
          {t("PCRENDER xem yêu cầu và gọi lại cho bạn để xác nhận, giao hàng và nhận cọc.")}
        </p>
      </div>

      {loading && !requests.length ? (
        <LoadingBlock />
      ) : !requests.length ? (
        <div className="flex flex-col items-center gap-1.5 px-4 py-12 text-center">
          <div className="mb-2 grid h-14 w-14 place-items-center rounded-2xl bg-brand-soft text-brand">
            <IcPackage className="h-[26px] w-[26px]" />
          </div>
          <h3 className="text-base font-bold">{t("Chưa gửi yêu cầu nào")}</h3>
          <p className="m-0 text-muted">{t("Chọn linh kiện ở trên và bấm Thuê để gửi yêu cầu.")}</p>
        </div>
      ) : (
        <div className="flex flex-col">
          {requests.map((r, i) => {
            const item = typeof r.componentId === "object" ? (r.componentId as PcComponent) : undefined;
            const cat = categoryOf(item?.category);
            const monthly = r.rentalType === "monthly";
            const unit = monthly ? t("tháng") : t("ngày");
            const start = dayjs(r.startDate);
            const end = start.add(r.duration, monthly ? "month" : "day");
            // Giá lúc gửi (rent_price, deposit). Yêu cầu cũ chưa có thì tạm tính theo giá hiện tại
            const rent =
              r.rent_price ||
              (item ? (monthly ? item.monthly_rental_rate : item.daily_rental_rate) * r.duration * r.quantity : 0);
            const deposit = r.deposit || (item?.deposit || 0) * r.quantity;
            const [label, tone] = STATUS[r.status] || [r.status || "Mới gửi", "warn"];

            return (
              <div
                key={r._id}
                className={clsx(
                  "grid grid-cols-[minmax(0,1.6fr)_minmax(0,1.4fr)_130px_150px_110px] items-center gap-3 px-5 py-3 max-[1100px]:grid-cols-2 max-[1100px]:gap-y-2 max-[760px]:grid-cols-1 max-[760px]:px-4",
                  i > 0 && "border-t border-line"
                )}
              >
                <div className="flex min-w-0 items-center gap-2.5">
                  <span
                    className="grid h-9 w-9 flex-none place-items-center rounded-[9px]"
                    style={{ background: `color-mix(in srgb, ${cat.color} 14%, transparent)`, color: cat.color }}
                  >
                    <cat.icon className="h-[18px] w-[18px]" />
                  </span>
                  <div className="min-w-0">
                    <b className="block truncate text-sm">
                      {item?.name || t("Linh kiện")}
                      {r.quantity > 1 && ` × ${r.quantity}`}
                    </b>
                    <div className="flex items-center gap-0.5 text-[11.5px] text-muted">
                      {t("Mã yêu cầu")}: <span className="font-mono">{r._id.slice(-8).toUpperCase()}</span>
                      <CopyButton value={r._id} label="Sao chép mã yêu cầu" />
                    </div>
                  </div>
                </div>
                <div className="flex min-w-0 flex-col text-[13px] tabular-nums">
                  <span>
                    {r.duration} {unit} · {start.format("DD/MM/YYYY")} → {end.format("DD/MM/YYYY")}
                  </span>
                  <small className="truncate text-[11.5px] text-faint" title={`${r.address}, ${r.location}`}>
                    {r.address}, {r.location}
                  </small>
                </div>
                <div className="flex min-w-0 flex-col text-[13px] tabular-nums">
                  <b>{rent.toLocaleString("vi-VN")}đ</b>
                  <small className="text-[11.5px] text-faint">
                    {t("Cọc")} {deposit.toLocaleString("vi-VN")}đ
                  </small>
                </div>
                <div className="flex flex-col items-start gap-1">
                  <StatusPill tone={tone}>{t(label)}</StatusPill>
                  {r.createdAt && <small className="text-[11.5px] tabular-nums text-faint">{dayjs(r.createdAt).format("DD/MM/YYYY HH:mm")}</small>}
                </div>
                <div className="text-right max-[1100px]:text-left">
                  {item && (
                    <button
                      type="button"
                      className={clsx(btn, btnGhost, btnSm)}
                      disabled={!item.in_stock}
                      title={item.in_stock ? undefined : t("Hết hàng")}
                      onClick={() => onAgain(item)}
                    >
                      <IcRefresh className="h-4 w-4" />
                      {t("Gửi lại")}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-end border-t border-line px-5 py-3">
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={onPageChange} />
        </div>
      )}
    </section>
  );
}

export default PcRequests;
